import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, ChevronLeft, ChevronRight, Copy, Loader2, Plus, X } from 'lucide-react';
import client, { apiError } from './adminApi';
import { useAdminAuth } from './AdminAuth';
import { cn } from '@/lib/utils';

const pad = (n) => String(n).padStart(2, '0');
const monthKey = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;

// Availability administration: open days, close days, block ranges, copy
// availability. All times are Europe/Dublin in the UI, UTC in the database.
// Public booking slots update immediately (server-calculated).
export default function AvailabilityPage() {
  const { user } = useAdminAuth();
  const [month, setMonth] = useState(() => monthKey(new Date()));
  const [data, setData] = useState(null);
  const [meetingTypes, setMeetingTypes] = useState([]);
  const [health, setHealth] = useState(null);
  const [creating, setCreating] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [form, setForm] = useState({ startTime: '09:30', endTime: '12:30', maxBookings: 1, meetingTypeId: 'initial-consultation', reason: '' });
  const [copyDates, setCopyDates] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const [av, mt, hl] = await Promise.all([
        client.get('/admin/booking/availability', { params: { month } }),
        client.get('/admin/booking/meeting-types'),
        client.get('/admin/booking/availability/health'),
      ]);
      setData(av.data);
      setMeetingTypes(mt.data.meetingTypes);
      setHealth(hl.data);
    } catch (err) {
      setError(apiError(err, 'Availability could not be loaded.'));
    }
  }, [month]);

  useEffect(() => { load(); }, [load]);

  const byDay = useMemo(() => {
    const map = {};
    if (!data) return map;
    data.windows.forEach((w) => {
      map[w.date] = map[w.date] || { windows: [], blocks: [], bookings: [] };
      map[w.date].windows.push(w);
    });
    data.blocks.forEach((b) => {
      map[b.date] = map[b.date] || { windows: [], blocks: [], bookings: [] };
      map[b.date].blocks.push(b);
    });
    data.bookings.forEach((b) => {
      map[b.date] = map[b.date] || { windows: [], blocks: [], bookings: [] };
      map[b.date].bookings.push(b);
    });
    return map;
  }, [data]);

  const cells = useMemo(() => {
    if (!month) return [];
    const [y, m] = month.split('-').map(Number);
    const first = new Date(Date.UTC(y, m - 1, 1));
    const daysInMonth = new Date(Date.UTC(y, m, 0)).getUTCDate();
    const blanks = (first.getUTCDay() + 6) % 7; // Monday start
    return [
      ...Array.from({ length: blanks }, () => null),
      ...Array.from({ length: daysInMonth }, (_, i) => `${month}-${pad(i + 1)}`),
    ];
  }, [month]);

  const act = async (fn, successMsg) => {
    if (busy) return;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await fn();
      setNotice(successMsg);
      await load();
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const openDay = () => act(
    () => client.post('/admin/booking/availability/open-day', {
      date: selectedDay, startTime: form.startTime, endTime: form.endTime,
      meetingTypeId: form.meetingTypeId, maxBookings: Number(form.maxBookings) || 1,
    }),
    `Opened ${selectedDay} ${form.startTime}–${form.endTime}`
  );

  const closeDay = () => act(
    () => client.post('/admin/booking/availability/close-day', { date: selectedDay }),
    `Closed ${selectedDay}`
  );

  const blockDay = (fullDay) => act(
    () => client.post('/admin/booking/availability/block', {
      date: selectedDay, startTime: fullDay ? null : form.startTime,
      endTime: fullDay ? null : form.endTime, reason: form.reason, blockType: 'manual',
    }),
    fullDay ? `Blocked all of ${selectedDay}` : `Blocked ${selectedDay} ${form.startTime}–${form.endTime}`
  );

  const copyToDates = () => {
    const targets = copyDates.split(',').map((s) => s.trim()).filter(Boolean);
    if (!targets.length) { setError('Enter target dates (YYYY-MM-DD, comma separated).'); return; }
    act(
      () => client.post('/admin/booking/availability/copy', { fromDate: selectedDay, toDates: targets }),
      `Copied ${selectedDay} availability`
    );
  };

  // One-click repair: move an open window to another meeting type (or "All
  // meeting types") without deleting and re-entering it.
  const reassign = (windowId, meetingTypeId) => act(
    () => client.patch(`/admin/booking/availability/window/${windowId}`, { meetingTypeId }),
    'Window reassigned'
  );

  const monthLabel = new Date(`${month}-15T12:00:00Z`).toLocaleDateString('en-IE', { month: 'long', year: 'numeric', timeZone: 'Europe/Dublin' });
  const shift = (dir) => {
    const [y, m] = month.split('-').map(Number);
    const d = new Date(Date.UTC(y, m - 1 + dir, 1));
    setMonth(monthKey(d));
    setSelectedDay(null);
  };

  return (
    <div data-testid="admin-availability">
      <h1 className="text-2xl font-extrabold tracking-tight text-navy-900">Availability</h1>
      <p className="mt-1 text-sm text-slate-500">
        Open and close appointment days. Times are Irish time (Europe/Dublin). Public slots update immediately.
      </p>

      {meetingTypes.length > 0 && (
        <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4" data-testid="meeting-types-panel">
          <div className="mb-2 flex items-center justify-between gap-2">
            <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400">Meeting types</p>
            {['owner', 'administrator'].includes(user.role) && (
              <button
                type="button"
                onClick={() => setCreating((c) => !c)}
                data-testid="mt-new-toggle"
                className="rounded-md bg-navy-900 px-3 py-1.5 text-xs font-bold text-white hover:bg-navy-700"
              >
                {creating ? 'Close' : '+ New Meeting Type'}
              </button>
            )}
          </div>
          {creating && (
            <NewMeetingTypeForm
              onCreated={() => { setCreating(false); load(); }}
            />
          )}
          {meetingTypes.map((mt) => (
            <MeetingTypeRow key={mt.meetingTypeId} mt={mt} onSaved={load} canEdit={['owner', 'administrator'].includes(user.role)} />
          ))}
        </div>
      )}

      {health && (
        <HealthPanel health={health} meetingTypes={meetingTypes} onReassign={reassign} busy={busy} />
      )}

      <div className="mt-5 grid gap-6 lg:grid-cols-[1fr_340px]">
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <div className="flex items-center justify-between">
            <button type="button" onClick={() => shift(-1)} aria-label="Previous month" data-testid="av-prev" className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-slate-300 hover:bg-navy-50">
              <ChevronLeft className="h-4 w-4" />
            </button>
            <p className="text-base font-extrabold text-navy-900" aria-live="polite" data-testid="av-month">{monthLabel}</p>
            <button type="button" onClick={() => shift(1)} aria-label="Next month" data-testid="av-next" className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-slate-300 hover:bg-navy-50">
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>

          {!data ? (
            <div className="flex justify-center py-14" role="status"><Loader2 className="h-5 w-5 animate-spin text-navy-900" /></div>
          ) : (
            <div className="mt-4 grid grid-cols-7 gap-1" role="grid" aria-label={`Availability for ${monthLabel}`}>
              {['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'].map((d) => (
                <div key={d} className="py-1 text-center text-[11px] font-bold uppercase text-slate-400">{d}</div>
              ))}
              {cells.map((day, i) => {
                if (!day) return <div key={`b-${i}`} aria-hidden="true" />;
                const info = byDay[day];
                const open = info?.windows.filter((w) => w.status === 'open') || [];
                const blocks = info?.blocks || [];
                const bookings = info?.bookings || [];
                const selected = selectedDay === day;
                return (
                  <button
                    key={day} type="button" onClick={() => setSelectedDay(day)}
                    aria-pressed={selected}
                    aria-label={`${day}: ${open.length} open windows, ${blocks.length} blocks, ${bookings.length} bookings`}
                    data-testid={`av-day-${day}`}
                    className={cn(
                      'flex min-h-[3.25rem] flex-col items-center justify-start gap-1 rounded-md border p-1.5 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy-900',
                      selected ? 'border-navy-900 bg-navy-50' : 'border-slate-200 hover:border-navy-300',
                    )}
                  >
                    <span className="font-bold text-navy-900">{parseInt(day.slice(-2), 10)}</span>
                    <span className="flex flex-wrap items-center justify-center gap-1">
                      {open.length > 0 && <span className="h-2 w-2 rounded-full bg-teal-600" title={`${open.length} open window(s)`} aria-hidden="true" />}
                      {blocks.length > 0 && <span className="h-2 w-2 rounded-full bg-red-500" title={`${blocks.length} block(s)`} aria-hidden="true" />}
                      {bookings.length > 0 && <span className="h-2 w-2 rounded-full bg-navy-900" title={`${bookings.length} booking(s)`} aria-hidden="true" />}
                    </span>
                    <span className="sr-only">
                      {open.length > 0 && `${open.length} open window${open.length > 1 ? 's' : ''}. `}
                      {blocks.length > 0 && `${blocks.length} block${blocks.length > 1 ? 's' : ''}. `}
                      {bookings.length > 0 && `${bookings.length} booking${bookings.length > 1 ? 's' : ''}.`}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
          <div className="mt-4 flex flex-wrap gap-4 text-xs font-semibold text-slate-600" aria-hidden="true">
            <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-teal-600" /> Open availability</span>
            <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-red-500" /> Blocked</span>
            <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-navy-900" /> Booking</span>
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-5 lg:self-start" data-testid="av-day-panel">
          {!selectedDay ? (
            <p className="text-sm text-slate-500">Select a day to open availability, block time or review bookings.</p>
          ) : (
            <>
              <h2 className="text-base font-extrabold text-navy-900">
                {new Date(`${selectedDay}T12:00:00Z`).toLocaleDateString('en-IE', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'Europe/Dublin' })}
              </h2>

              {(byDay[selectedDay]?.windows || []).map((w) => (
                <div key={w.windowId} className="mt-3 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-navy-900">
                      {w.startTime}–{w.endTime} · {w.meetingTypeId === '*' ? 'All meeting types' : (meetingTypes.find((t) => t.meetingTypeId === w.meetingTypeId)?.name || w.meetingTypeId)} · {w.status} · max {w.maxBookings}
                      {w.source === 'dev-seed' && <span className="ml-1 text-amber-700">(dev seed)</span>}
                    </span>
                    {w.status === 'open' && (
                      <button type="button" onClick={() => act(() => client.delete(`/admin/booking/availability/window/${w.windowId}`), 'Window closed')} data-testid={`av-close-window-${w.windowId}`} className="font-bold text-red-700 hover:underline">
                        Close
                      </button>
                    )}
                  </div>
                  {w.status === 'open' && (
                    <div className="mt-2 flex items-center gap-2">
                      <span className="font-semibold text-slate-500">Bookable for</span>
                      <select
                        value={w.meetingTypeId}
                        onChange={(e) => reassign(w.windowId, e.target.value)}
                        disabled={busy}
                        data-testid={`av-reassign-${w.windowId}`}
                        aria-label="Reassign window to meeting type"
                        className="w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-xs"
                      >
                        <option value="*">All meeting types</option>
                        {meetingTypes.map((mt) => <option key={mt.meetingTypeId} value={mt.meetingTypeId}>{mt.name}</option>)}
                        {!['*', ...meetingTypes.map((t) => t.meetingTypeId)].includes(w.meetingTypeId) && (
                          <option value={w.meetingTypeId}>{w.meetingTypeId} (removed type)</option>
                        )}
                      </select>
                    </div>
                  )}
                </div>
              ))}
              {(byDay[selectedDay]?.blocks || []).map((b) => (
                <div key={b.blockId} className="mt-2 flex items-center justify-between rounded-md border border-red-100 bg-red-50 px-3 py-2 text-xs">
                  <span className="font-semibold text-red-800">Blocked {b.startTime}{b.endTime ? `–${b.endTime}` : ' (all day)'}{b.reason ? ` — ${b.reason}` : ''}</span>
                  <button type="button" onClick={() => act(() => client.delete(`/admin/booking/availability/block/${b.blockId}`), 'Block removed')} className="font-bold text-red-700 hover:underline" aria-label="Remove block">
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
              {(byDay[selectedDay]?.bookings || []).map((b) => (
                <div key={b.bookingId} className="mt-2 rounded-md border border-navy-100 bg-navy-50 px-3 py-2 text-xs">
                  <span className="font-semibold text-navy-900">{b.startTime}–{b.endTime} · {b.customerName} · {b.reference}</span>
                </div>
              ))}

              <div className="mt-5 space-y-3 border-t border-slate-200 pt-4">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label htmlFor="av-start" className="text-xs font-bold text-navy-900">Start</label>
                    <input id="av-start" type="time" value={form.startTime} onChange={(e) => setForm({ ...form, startTime: e.target.value })} data-testid="av-start" className="mt-1 w-full rounded-md border border-slate-300 px-2.5 py-2 text-sm" />
                  </div>
                  <div>
                    <label htmlFor="av-end" className="text-xs font-bold text-navy-900">End</label>
                    <input id="av-end" type="time" value={form.endTime} onChange={(e) => setForm({ ...form, endTime: e.target.value })} data-testid="av-end" className="mt-1 w-full rounded-md border border-slate-300 px-2.5 py-2 text-sm" />
                  </div>
                </div>
                <div>
                  <label htmlFor="av-type" className="text-xs font-bold text-navy-900">Meeting type</label>
                  <select id="av-type" value={form.meetingTypeId} onChange={(e) => setForm({ ...form, meetingTypeId: e.target.value })} data-testid="av-type" className="mt-1 w-full rounded-md border border-slate-300 px-2.5 py-2 text-sm">
                    <option value="*">All meeting types</option>
                    {meetingTypes.map((mt) => <option key={mt.meetingTypeId} value={mt.meetingTypeId}>{mt.name}</option>)}
                  </select>
                </div>
                <div>
                  <label htmlFor="av-reason" className="text-xs font-bold text-navy-900">Block reason (internal only)</label>
                  <input id="av-reason" type="text" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="av-reason" className="mt-1 w-full rounded-md border border-slate-300 px-2.5 py-2 text-sm" placeholder="e.g. Annual leave" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <button type="button" onClick={openDay} disabled={busy} data-testid="av-open-day" className="inline-flex items-center justify-center gap-1.5 rounded-md bg-navy-900 px-3 py-2.5 text-xs font-bold text-white hover:bg-navy-700 disabled:opacity-50">
                    <Plus className="h-3.5 w-3.5" /> Open Day
                  </button>
                  <button type="button" onClick={() => blockDay(false)} disabled={busy} data-testid="av-block-range" className="rounded-md border-2 border-navy-900 px-3 py-2 text-xs font-bold text-navy-900 hover:bg-navy-50 disabled:opacity-50">
                    Block Range
                  </button>
                  <button type="button" onClick={() => blockDay(true)} disabled={busy} data-testid="av-block-day" className="rounded-md border border-red-300 px-3 py-2 text-xs font-bold text-red-700 hover:bg-red-50 disabled:opacity-50">
                    Block Full Day
                  </button>
                  <button type="button" onClick={closeDay} disabled={busy} data-testid="av-close-day" className="rounded-md border border-slate-300 px-3 py-2 text-xs font-bold text-navy-900 hover:bg-navy-50 disabled:opacity-50">
                    Close Day
                  </button>
                </div>
                <div>
                  <label htmlFor="av-copy" className="text-xs font-bold text-navy-900">Copy this day to dates</label>
                  <div className="mt-1 flex gap-2">
                    <input id="av-copy" type="text" value={copyDates} onChange={(e) => setCopyDates(e.target.value)} placeholder="2026-09-15, 2026-09-16" data-testid="av-copy-dates" className="w-full rounded-md border border-slate-300 px-2.5 py-2 text-sm" />
                    <button type="button" onClick={copyToDates} disabled={busy} data-testid="av-copy-btn" aria-label="Copy availability" className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-navy-900 text-white hover:bg-navy-700 disabled:opacity-50">
                      <Copy className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}
          {notice && <p role="status" className="mt-4 rounded-md border border-teal-200 bg-teal-50 px-3 py-2 text-xs font-semibold text-teal-800" data-testid="av-notice">{notice}</p>}
          {error && <p role="alert" className="mt-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs font-semibold text-red-700" data-testid="av-error">{error}</p>}
        </div>
      </div>
    </div>
  );
}

// Booking health: for each active meeting type, shows whether visitors can
// genuinely book (the slot engine's verdict), the next available slot, and a
// plain-language warning when open days are invisible to visitors. Orphaned
// windows (linked to removed/inactive types) can be repaired in one click.
function HealthPanel({ health, meetingTypes, onReassign, busy }) {
  const fmtSlot = (slot) =>
    new Date(slot.startUtc).toLocaleString('en-IE', {
      weekday: 'short', day: 'numeric', month: 'short',
      hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Dublin',
    });
  const orphans = health.orphanedWindows || [];
  return (
    <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4" data-testid="booking-health-panel">
      <p className="mb-2 text-xs font-bold uppercase tracking-[0.14em] text-slate-400">Booking health</p>
      <div className="space-y-2">
        {health.meetingTypes.map((h) => (
          <div key={h.meetingTypeId} className="flex items-start gap-2.5 text-sm" data-testid={`health-row-${h.meetingTypeId}`}>
            <span
              className={cn('mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full', h.status === 'ok' ? 'bg-teal-600' : 'bg-amber-500')}
              aria-hidden="true"
            />
            <div>
              <p className="font-bold text-navy-900">{h.name}</p>
              {h.status === 'ok' ? (
                <p className="text-xs font-semibold text-teal-800" data-testid={`health-status-${h.meetingTypeId}`}>
                  Bookable — {h.bookableDays} day{h.bookableDays === 1 ? '' : 's'} available
                  {h.nextSlot && ` · next: ${fmtSlot(h.nextSlot)}`}
                </p>
              ) : (
                h.warnings.map((w, i) => (
                  <p key={i} className="text-xs font-semibold text-amber-800" role="alert" data-testid={`health-warning-${h.meetingTypeId}`}>
                    {w}
                  </p>
                ))
              )}
            </div>
          </div>
        ))}
      </div>
      {orphans.length > 0 && (
        <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-3" data-testid="orphaned-windows">
          <p className="flex items-center gap-1.5 text-xs font-bold text-amber-900">
            <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" />
            {orphans.length} open day{orphans.length === 1 ? ' is' : 's are'} invisible to visitors
          </p>
          <p className="mt-1 text-xs text-amber-800">
            These days are linked to a meeting type that no longer exists or is inactive. Reassign them to make them bookable.
          </p>
          {orphans.map((w) => (
            <div key={w.windowId} className="mt-2 flex flex-wrap items-center gap-2 rounded-md border border-amber-200 bg-white px-3 py-2 text-xs" data-testid={`orphan-row-${w.windowId}`}>
              <span className="font-semibold text-navy-900">
                {w.date} · {w.startTime}–{w.endTime}
                <span className="ml-1 font-normal text-slate-500">({w.orphanReason})</span>
              </span>
              <select
                defaultValue=""
                onChange={(e) => e.target.value && onReassign(w.windowId, e.target.value)}
                disabled={busy}
                data-testid={`orphan-reassign-${w.windowId}`}
                aria-label={`Reassign ${w.date} to a meeting type`}
                className="rounded-md border border-slate-300 bg-white px-2 py-1.5 text-xs"
              >
                <option value="" disabled>Reassign to…</option>
                <option value="*">All meeting types</option>
                {meetingTypes.map((mt) => <option key={mt.meetingTypeId} value={mt.meetingTypeId}>{mt.name}</option>)}
              </select>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function NewMeetingTypeForm({ onCreated }) {
  // Sensible location defaults per format — the user can still type anything.
  const FORMAT_LOCATIONS = {
    online: 'Online (video call)',
    'in-person': 'Amden Group office, Unit 39 Kells Business Park',
    phone: 'Phone call',
  };
  const [form, setForm] = useState({
    name: '', description: '', durationMinutes: 30, bufferAfterMinutes: 10,
    dailyBookingLimit: 6, minNoticeHours: 12, maxAdvanceDays: 45,
    format: 'online', location: 'Online (video call)', status: 'active',
  });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const changeFormat = (format) =>
    setForm((f) => ({
      ...f,
      format,
      // keep a typed custom location; only swap while it still holds a default
      location: Object.values(FORMAT_LOCATIONS).includes(f.location) ? FORMAT_LOCATIONS[format] : f.location,
    }));
  const create = async () => {
    setBusy(true);
    setMsg('');
    try {
      await client.post('/admin/booking/meeting-types', {
        ...form,
        durationMinutes: Number(form.durationMinutes),
        bufferAfterMinutes: Number(form.bufferAfterMinutes),
        dailyBookingLimit: Number(form.dailyBookingLimit),
        minNoticeHours: Number(form.minNoticeHours),
        maxAdvanceDays: Number(form.maxAdvanceDays),
      });
      onCreated();
    } catch (err) {
      setMsg(apiError(err));
      setBusy(false);
    }
  };
  return (
    <div className="mb-3 rounded-md border border-navy-200 bg-navy-50/50 p-3" data-testid="mt-create-form">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <div className="col-span-2">
          <label className="text-[11px] font-bold text-slate-500" htmlFor="mt-new-name">Name</label>
          <input id="mt-new-name" type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Strategy Session" data-testid="mt-new-name" className="mt-0.5 w-full rounded-md border border-slate-300 px-2 py-1.5 text-xs" />
        </div>
        <div className="col-span-2">
          <label className="text-[11px] font-bold text-slate-500" htmlFor="mt-new-format">Format</label>
          <select id="mt-new-format" value={form.format} onChange={(e) => changeFormat(e.target.value)} data-testid="mt-new-format" className="mt-0.5 w-full rounded-md border border-slate-300 px-2 py-1.5 text-xs">
            <option value="online">Online</option>
            <option value="in-person">In person</option>
            <option value="phone">Phone</option>
          </select>
        </div>
        <div className="col-span-2">
          <label className="text-[11px] font-bold text-slate-500" htmlFor="mt-new-location">Location (shown to visitors)</label>
          <input id="mt-new-location" type="text" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="mt-new-location" className="mt-0.5 w-full rounded-md border border-slate-300 px-2 py-1.5 text-xs" />
        </div>
        <div className="col-span-2 sm:col-span-4">
          <label className="text-[11px] font-bold text-slate-500" htmlFor="mt-new-description">Description (shown to visitors)</label>
          <input id="mt-new-description" type="text" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="What this meeting is for" data-testid="mt-new-description" className="mt-0.5 w-full rounded-md border border-slate-300 px-2 py-1.5 text-xs" />
        </div>
        {[['durationMinutes', 'Duration (min)'], ['bufferAfterMinutes', 'Buffer after (min)'], ['dailyBookingLimit', 'Daily limit'], ['minNoticeHours', 'Min notice (hours)'], ['maxAdvanceDays', 'Max advance (days)']].map(([key, label]) => (
          <div key={key}>
            <label className="text-[11px] font-bold text-slate-500" htmlFor={`mt-new-${key}`}>{label}</label>
            <input id={`mt-new-${key}`} type="number" value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })} data-testid={`mt-new-${key}`} className="mt-0.5 w-full rounded-md border border-slate-300 px-2 py-1.5 text-xs" />
          </div>
        ))}
        <div className="col-span-2 sm:col-span-1">
          <label className="text-[11px] font-bold text-slate-500" htmlFor="mt-new-status">Status</label>
          <select id="mt-new-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="mt-new-status" className="mt-0.5 w-full rounded-md border border-slate-300 px-2 py-1.5 text-xs">
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      </div>
      <div className="mt-3 flex items-center gap-2">
        <button type="button" onClick={create} disabled={busy || form.name.trim().length < 2} data-testid="mt-create-save" className="rounded-md bg-gold-600 px-3 py-2 text-xs font-bold text-navy-950 hover:bg-gold-500 disabled:opacity-50">
          {busy ? 'Creating…' : 'Create Meeting Type'}
        </button>
        {msg && <span className="text-xs font-semibold text-red-700" role="alert" data-testid="mt-create-error">{msg}</span>}
      </div>
      <p className="mt-2 text-[11px] text-slate-500">Active types appear immediately in the public booking journey. Paid options arrive with payments.</p>
    </div>
  );
}

function MeetingTypeRow({ mt, onSaved, canEdit }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(mt);
  const [msg, setMsg] = useState('');
  const save = async () => {
    try {
      await client.patch(`/admin/booking/meeting-types/${mt.meetingTypeId}`, {
        name: form.name, description: form.description,
        durationMinutes: Number(form.durationMinutes),
        bufferBeforeMinutes: Number(form.bufferBeforeMinutes),
        bufferAfterMinutes: Number(form.bufferAfterMinutes),
        minNoticeHours: Number(form.minNoticeHours),
        maxAdvanceDays: Number(form.maxAdvanceDays),
        dailyBookingLimit: Number(form.dailyBookingLimit),
        format: form.format,
        location: form.location,
        status: form.status,
      });
      setMsg('Saved.');
      setEditing(false);
      onSaved();
    } catch (err) {
      setMsg(apiError(err));
    }
  };
  return (
    <div className="text-sm" data-testid={`meeting-type-${mt.meetingTypeId}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="font-bold text-navy-900">
          {mt.name} <span className="font-normal text-slate-500">· {mt.durationMinutes} min · +{mt.bufferAfterMinutes}m buffer · limit {mt.dailyBookingLimit}/day · {mt.status}</span>
        </p>
        {canEdit && (
          <button type="button" onClick={() => setEditing((e) => !e)} data-testid="mt-edit-toggle" className="text-xs font-bold text-navy-900 underline-offset-2 hover:underline">
            {editing ? 'Close' : 'Edit'}
          </button>
        )}
      </div>
      {editing && (
        <div className="mt-3 grid grid-cols-2 gap-2 rounded-md bg-slate-50 p-3 sm:grid-cols-4">
          {[['name', 'Name', 'text'], ['durationMinutes', 'Duration (min)', 'number'], ['bufferAfterMinutes', 'Buffer after (min)', 'number'], ['dailyBookingLimit', 'Daily limit', 'number'], ['minNoticeHours', 'Min notice (hours)', 'number'], ['maxAdvanceDays', 'Max advance (days)', 'number']].map(([key, label, type]) => (
            <div key={key}>
              <label className="text-[11px] font-bold text-slate-500" htmlFor={`mt-${key}`}>{label}</label>
              <input id={`mt-${key}`} type={type} value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })} className="mt-0.5 w-full rounded-md border border-slate-300 px-2 py-1.5 text-xs" />
            </div>
          ))}
          <div>
            <label className="text-[11px] font-bold text-slate-500" htmlFor="mt-format">Format</label>
            <select id="mt-format" value={form.format || 'online'} onChange={(e) => setForm({ ...form, format: e.target.value })} data-testid="mt-format" className="mt-0.5 w-full rounded-md border border-slate-300 px-2 py-1.5 text-xs">
              <option value="online">Online</option>
              <option value="in-person">In person</option>
              <option value="phone">Phone</option>
            </select>
          </div>
          <div className="col-span-2">
            <label className="text-[11px] font-bold text-slate-500" htmlFor="mt-location">Location (shown to visitors)</label>
            <input id="mt-location" type="text" value={form.location || ''} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="mt-location" className="mt-0.5 w-full rounded-md border border-slate-300 px-2 py-1.5 text-xs" />
          </div>
          <div>
            <label className="text-[11px] font-bold text-slate-500" htmlFor="mt-status">Status</label>
            <select id="mt-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="mt-0.5 w-full rounded-md border border-slate-300 px-2 py-1.5 text-xs">
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          <div className="col-span-2 flex items-end gap-2 sm:col-span-4">
            <button type="button" onClick={save} data-testid="mt-save" className="rounded-md bg-navy-900 px-3 py-2 text-xs font-bold text-white hover:bg-navy-700">Save Meeting Type</button>
            {msg && <span className="text-xs font-semibold text-slate-600" role="status">{msg}</span>}
          </div>
        </div>
      )}
    </div>
  );
}
